<template>
  <section>
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <h1 class="title">{{ $t('entity.sessions') }}</h1>
        <button class="btn btn-lg btn-outline-primary ml-4" @click="goToAllSessions">
          {{ $t('sessionStatus.allSession') }}
        </button>
      </div>
      <div class="d-flex align-items-center">
        <div class="btn-group">
          <button
            type="button"
            class="btn btn-outline-none btn-with-svg-icon"
            :class="displayMode === 'full-width' ? 'btn-secondary-light' : 'btn-outline-secondary'"
            @click="setViewMiniature('full-width')"
          >
            <svg-icon :name="'menu_one_item'" :width="22" :height="22"></svg-icon>
          </button>
          <button
            type="button"
            class="btn btn-outline-none btn-with-svg-icon"
            :class="displayMode === 'two-columns' ? 'btn-secondary-light' : 'btn-outline-secondary'"
            @click="setViewMiniature('two-columns')"
          >
            <svg-icon :name="'menu_two_item'" :width="22" :height="22"></svg-icon>
          </button>
          <button
            type="button"
            class="btn btn-outline-none btn-with-svg-icon"
            :class="displayMode === 'three-columns' ? 'btn-secondary-light' : 'btn-outline-secondary'"
            @click="setViewMiniature('three-columns')"
          >
            <svg-icon :name="'menu_three_item'" :width="22" :height="22"></svg-icon>
          </button>
        </div>

        <button
          type="button"
          class="btn btn-outline-none btn-with-svg-icon ml-3"
          :class="eventsInfoView ? 'btn-secondary-light' : 'btn-outline-secondary'"
          @click="setViewEventsInfo"
        >
          <svg-icon :name="'warning'" :width="22" :height="22"></svg-icon>
        </button>
      </div>
    </div>
    <div style="display: flex; gap: 24px">
      <div id="container" class="miniature-sessions-container">
        <div v-if="!hasTrainings" class="empty-list">
          <svg-icon
            class="svg-icon-without-hover svg-icon-big"
            :name="'empty-list'"
            :height="110"
            :width:="110"
          ></svg-icon>
          <div class="text-larger text-light">{{ $t('messageInfo.1') }}</div>
          <div class="text-light">
            {{ $t('messageInfo.2') }} <a href="sessions">{{ $t('messageInfo.3') }}</a>
          </div>
        </div>
        <active-training-miniature
          v-else
          v-for="train in trainings"
          :key="train.id"
          :training="train"
          :displayMode="displayMode"
          :eventsInfoView="eventsInfoView"
          @remove:trainingFromTheList="removeTrainingFromTheList"
          @add:trainingToTheList="addTrainingToTheList"
        >
        </active-training-miniature>
      </div>

      <incoming-events-list
        v-if="activeTrainingsId"
        v-show="eventsInfoView"
        :events="events"
        :trainingsWithPatientName="activeTrainingsId"
        @show:notification="showNotification"
      ></incoming-events-list>

      <notifications position="bottom right" :duration="5000" group="foo">
        <template slot="body" slot-scope="props">
          <div class="custom-template card">
            <div class="custom-template-content">
              <h2 class="custom-template-title">
                <div>
                  {{ props.item.data.namePatient }}
                </div>
              </h2>
              <div class="custom-template-text ">
                <span
                  class="abbr mr-1"
                  :class="props.item.data.color ? 'badge-' + props.item.data.color : 'badge-danger'"
                  v-html="props.item.data.eventAbbr"
                ></span>
                <span class="text tool-tip-td">
                  {{ props.item.data.eventTitle }}
                </span>
                <div style="margin-left: 50px; font-weight: bolder;">
                  {{ props.item.data.eventTextValue }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </notifications>
      <audio ref="notificationSound" src="@/assets/notification.mp3"></audio>
    </div>
  </section>
</template>

<script>
import http from '@/modules/http.js'
import { stringify } from 'qs'
import '@/styles/elements/miniature-session.less'
import { fullName } from '@/mixins/full-name.js'
import { userRights } from '@/mixins/user-rights.js'
import { eventTypes } from '@/main.config.js'
import { eventsConfig } from '@/events.config.js'
import timeString from '@/modules/time-string.js'

export default {
  name: 'ActiveTrainingsMultiple',
  mixins: [fullName, userRights],
  components: {
    ActiveTrainingMiniature: () => import('@/components/Training/ActiveTrainingMiniature.vue'),
    IncomingEventsList: () => import('@/components/IncomingEventsList.vue'),
    SvgIcon: () => import('@/components/SvgIcon.vue')
  },
  data: () => ({
    activeTrainingsId: null,
    events: [],
    eventsInfoView: true,
    displayMode: localStorage.getItem('activeTrainingsDisplayMode') ?? 'full-width',
    trainings: null
  }),
  computed: {
    hasTrainings() {
      return this.trainings && this.trainings.length > 0
    }
  },
  mounted() {
    this.loadActiveSessions()
    this.eventsInfoView = false
  },
  methods: {
    goToAllSessions() {
      this.$router.push({ name: 'sessions' })
    },
    loadActiveSessions() {
      let params = {}
      params['training.status'] = {
        $in: [2]
      }
      params['$limit'] = -1
      params['$skip'] = 0
      return new Promise(resolve => {
        http
          .get('/trainings', {
            params: params,
            paramsSerializer: params =>
              stringify(params, {
                arrayFormat: 'indices',
                encodeValuesOnly: true
              })
          })
          .then(response => response.data)
          .then(trainings => {
            this.trainings = this.filterOutHangTrainings(trainings)  //Уберем из списка подвисшие тренировки, если есть более новые для того же курса
            this.activeTrainingsId = this.trainings.map(item => {
              return {
                patient_name: this.getSurnameWithInitials(item.patient),
                id: item.id
              }
            })
            resolve()
          })
          .then(() => {
            this.getPreviousEvents()
          })
          .catch(error => console.log(error))
      })
    },
    filterOutHangTrainings(trainings) {
      const trainingMap = new Map()
      trainings.forEach(training => {
        const existingTraining = trainingMap.get(training.course_id)

        if (!existingTraining) {
          trainingMap.set(training.course_id, training)
        }
        else if (existingTraining.date_start < training.date_start) {
          trainingMap.set(training.course_id, training)
        }
      })
      return Array.from(trainingMap.values())
    },
    getPreviousEvents() {
      this.activeTrainingsId.forEach(tr => {
        let tr_id = tr.id
        http
          .get('/ongoing-training/' + tr.id, {
            params: {
              type: 'ce'
            }
          })
          .then(response => {
            if (response.data.total > 0) {
              response.data.data.forEach(event => {
                if (event.type === eventTypes.cardioEvent) {
                  this.eventProcessing(event, tr_id)
                }
              })
            }
          })
      })
    },
    eventProcessing(event, tr_id) {
      if (!Object.hasOwn(eventsConfig, event.episodeType))
        return

      let time = timeString(Math.round(event.time), true)
      let eventObj = {
        isDuration: eventsConfig[event.episodeType].duration,
        episodeType: event.episodeType,
        text: eventsConfig[event.episodeType].text,
        value:
          event.episodeType === 'UserEvent'
            ? `${this.$t('sessionsInfo.heartRate')}: ${event.value}`
            : event.value,
        abbr: eventsConfig[event.episodeType].abbr,
        color: eventsConfig[event.episodeType].color,
        time: time,
        patientName: this.getPatientNameByTrainingId(tr_id),
        unit: event.episodeType === 'UserEvent' ? this.$t('unit.bpm') : eventsConfig[event.episodeType].unit
      }
      this.events.push(eventObj)
    },
    getPatientNameByTrainingId(trainingId) {
      const foundObject = this.activeTrainingsId.find(item => item.id === trainingId)
      if (foundObject) {
        return foundObject.patient_name
      } else {
        return 'Not found'
      }
    },
    removeTrainingFromTheList(train_id) {
      let idx = this.trainings.findIndex(el => el.id === train_id)
      this.trainings.splice(idx, 1)
    },
    addTrainingToTheList() {
      this.loadActiveSessions()
    },
    setViewMiniature(mode) {
      this.displayMode = mode
      localStorage.setItem('activeTrainingsDisplayMode', mode)
    },
    setViewEventsInfo() {
      this.eventsInfoView = !this.eventsInfoView
    },
    showNotification(event) {
      let eventTextValue = ''
      if (event.value) {
        eventTextValue += `${event.value}`
        if (event.unit) eventTextValue += `${event.unit}`
      }
      const key = Math.random()
        .toString(36)
        .substr(2, 9)
      this.$notify({
        group: 'foo',
        title: 'Новое кардиособытие',
        text: 'Hello user! This is a notification!',
        data: {
          eventAbbr: event.abbr,
          eventTitle: event.text,
          namePatient: event.patientName,
          color: event.color,
          eventTextValue: eventTextValue
        },
        key: key
      })
      this.playNotificationSound()
    },
    playNotificationSound() {
      const audio = this.$refs.notificationSound
      const playPromise = audio.play()

      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            // Звук успешно воспроизведен
          })
          .catch(error => {
            console.error('Ошибка воспроизведения звука:', error)
          })
      }
    }
  }
}
</script>

<style scoped>
.custom-template {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;

  text-align: left;
  font-size: 14px;
  margin: 5px;
  align-items: center;
  &,
  & > div {
    box-sizing: border-box;
  }

  .custom-template-content {
    padding: 10px;
  }

  .custom-template-title {
    font-size: 16px;
    font-weight: 400;
  }

  .custom-template-text {
    .abbr {
      border-radius: 12.5px;
      min-width: 40px;
      text-align: center;
      padding: 0 6px;
    }
  }

  .close-button {
    position: absolute;
    top: 5px;
    right: 5px;
    background: transparent;
    border: none;
    color: #888;
    font-size: 16px;
    cursor: pointer;
  }
}
</style>
